import {
  Editor,
  Transforms,
  Text,
  Location,
  Element,
  Range,
  BaseElement,
  Path,
  Node,
  Point,
} from 'slate';
import { ReactEditor } from 'slate-react';
import { blockLinkType } from '../plugins/blockLink/blockLinkType';
import { inlineLinkType } from '../plugins/inlineLink/inlineLinkType';
import { TextColourKey } from '../plugins/richtext/colours';
import { AulaEditorInstance, RichTextMark, AulaRange } from '../types';
import { getCurrentNode, hasRichTextMark, getTextColour } from './queries';

export const toggleBlockType = (
  editor: AulaEditorInstance,
  format: string | undefined,
  lastActiveSelection: Range,
) => {
  Transforms.select(editor, lastActiveSelection);

  const node = getCurrentNode(editor);
  const type = Element.isElement(node) && node.type === format ? '' : format;

  Transforms.setNodes(
    editor,
    {},
    {
      mode: 'lowest',
      match: (child: any) => child.b === true,
    },
  );

  Transforms.setNodes(editor, {
    type,
  });

  // setting timeout ensures that focus is not lost after
  // a re-render. Ideally it should be possible to preserve
  // selection too and there should be no timeout at all.
  setTimeout(() => ReactEditor.focus(editor), 0);
};

export const toggleRichTextMark = (
  editor: AulaEditorInstance,
  richTextMark: RichTextMark,
  lastActiveSelection: AulaRange,
) => {
  Transforms.select(editor, lastActiveSelection);
  const isActive = hasRichTextMark(editor, richTextMark);
  Transforms.setNodes(
    editor,
    { [richTextMark]: isActive ? null : true },
    { match: Text.isText, split: true },
  );

  ReactEditor.focus(editor);
};

export const toggleTextColourMark = (
  editor: AulaEditorInstance,
  colourKey: TextColourKey,
  lastActiveSelection: AulaRange,
) => {
  Transforms.select(editor, lastActiveSelection);
  const selectedColour = getTextColour(editor);
  const toggleColour = selectedColour === colourKey;

  Transforms.setNodes(
    editor,
    { c: toggleColour ? undefined : colourKey },
    { match: Text.isText, split: true },
  );

  ReactEditor.focus(editor);
};

export const insertVoidElement = (
  editor: Editor,
  element: Omit<Element, 'children'>,
  range?: Range,
): void => {
  const selection = range || editor.selection;

  if (selection == null) return;

  const isLineEnd = Editor.isEnd(
    editor,
    selection.anchor,
    selection.anchor.path,
  );

  const isLastNode = selection.anchor.path[0] === editor.children.length - 1;

  Transforms.splitNodes(editor, { at: range });

  // If there current node is empty then remove it, otherwise we'll be left with an extra empty line
  const currentNode = getCurrentNode(editor);
  const isNodeEmpty =
    Element.isElement(currentNode) && Editor.isEmpty(editor, currentNode);
  const isEditorEmpty = editor.children.length === 1 && isNodeEmpty;

  // TODO: We should be able to remove this?
  let { selection: selectionAfterSplit } = editor;
  if (selectionAfterSplit == null) {
    selectionAfterSplit = selection;
  }

  const selectedNode = selectionAfterSplit.anchor.path.slice(0, 1);
  const insertAt =
    !isEditorEmpty && isLineEnd
      ? selectedNode.map((idx) => idx + 1)
      : selectedNode;

  Transforms.insertNodes(
    editor,
    [
      {
        ...element,
        children: [],
      },
      // If we are inserting at the end of the last line in the document insert a empty line.
      ...(isLastNode && isLineEnd
        ? [
            {
              children: [{ text: '' }],
            },
          ]
        : []),
    ],
    {
      at: insertAt,
    },
  );

  if (isNodeEmpty) {
    Transforms.removeNodes(editor);
  }

  // in order to make it possible for Transforms.move to work, we need to have a selection,
  // but empty editor doesn't have a selection. Therefore, we add it here.
  if (!editor.selection) {
    editor.selection = {
      anchor: {
        path: [0, 0],
        offset: 0,
      },
      focus: {
        path: [0, 0],
        offset: 0,
      },
    };
  }

  if (isLineEnd) {
    // Leave cursor below the inserted block instead of above
    // If we were at the end of a line, the cursor will have been left above rather than below
    // the inserted block. Move it below the inserted block.
    Transforms.move(editor, { distance: 2, unit: 'line' });
    // Set the offset to 0 in cases where it isn't. We always want the offset to be 0 after inserting
    // a void element
    Transforms.setSelection(editor, {
      anchor: {
        ...editor.selection.anchor,
        offset: 0,
      },
      focus: {
        ...editor.selection.anchor,
        offset: 0,
      },
    });
  }
};

export const removeVoidElement = (
  editor: AulaEditorInstance,
  element: Element,
): void => {
  Transforms.delete(editor, {
    at: ReactEditor.findPath(editor, element),
    voids: true,
  });

  // if this was the last remaining element, add an empty element
  if (editor.children.length === 0) {
    Transforms.insertNodes(editor, [
      {
        children: [{ text: '' }],
      },
    ]);
  }
  ReactEditor.focus(editor);
};

export const insertBlock = (
  editor: AulaEditorInstance,
  path: Path,
  nodes: Node[],
) => {
  ReactEditor.focus(editor);

  // Find the top-level node where the block menu was accessed
  const currentNode = Editor.node(editor, path, { depth: 1 })[0];
  // Find the end of the line where the block menu was accessed
  const end = Editor.end(editor, path);

  // Determine if the current node is empty. We need this because if the current node is empty we
  // want it to appear like the content was added to this line, whereas if the current
  // node is not empty we want to insert the template content after the current lines
  const isNodeEmpty =
    Element.isElement(currentNode) &&
    Editor.isEmpty(editor, currentNode) &&
    !currentNode.type;

  // Insert nodes at the end of the current node
  Transforms.insertNodes(editor, nodes, { at: end, mode: 'highest' });

  if (isNodeEmpty) {
    // If the curremt node was empty, we now have an extra empty line (because we insert at
    // the end of the current node, which creates a new node below it). Remove the leftover empty
    // line
    Transforms.removeNodes(editor, { at: path });
  }

  // We want to leave the selection at the start of the inserted nodes. Find that point, accounting
  // for the fact we may or may not have been inserting into an empty node
  const nextSelection = isNodeEmpty
    ? Editor.start(editor, path)
    : Editor.after(editor, end, {
        voids: true,
        unit: 'block',
      });

  if (Point.isPoint(nextSelection)) {
    // Set the selection and refocus the editor. Timeout is required to work-around Slate issues
    // with setting the selection
    setTimeout(() => {
      Transforms.select(editor, {
        anchor: nextSelection,
        focus: nextSelection,
      });
      ReactEditor.focus(editor);
    }, 50);
  }
};

export const removeLink = (editor: AulaEditorInstance) => {
  Transforms.unwrapNodes(editor, {
    at: editor.selection as Range,
    match: (n) =>
      !Editor.isEditor(n) && Element.isElement(n) && n.type === 'inlineLink',
  });
};

export const collapseLinkBlock = (
  editor: AulaEditorInstance,
  at: Location,
  url: string,
) => {
  const [element] = Editor.node(editor, at);

  if (Element.isElement(element) && element?.type !== blockLinkType) return;

  Transforms.removeNodes(editor, {
    at,
  });

  Transforms.insertNodes(
    editor,
    {
      type: undefined,
      children: [
        {
          type: inlineLinkType,
          url,
          children: [
            {
              text: url,
            },
          ],
        },
      ],
    },
    {
      at,
    },
  );

  const prevNode = Editor.previous(editor, { at });

  // if the previous node is a text node, and the user is 'mid sentence', defined
  // as the text node ending in a space character, we collapse the block link
  // by merging it to the above text node.
  if (prevNode?.[0] && !Editor.isVoid(editor, prevNode[0])) {
    const matches = prevNode[0] as BaseElement;
    const firstChild = matches.children[0];
    const previousText = Text.isText(firstChild) && firstChild.text;

    if (previousText && previousText.slice(-1) === ' ') {
      Transforms.mergeNodes(editor, {
        at,
      });
    }
  }
};
